import { Component } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import Pagination from "@mui/material/Pagination";
import Autocomplete from "@mui/material/Autocomplete";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";
import { useMaterialUIController } from "context";
import AddInterstitialAdModal from "./components/AddInterstitialAdModal/AddInterstitialAdModal";
import InterstitialAdCard from "./components/InterstitialAdCard/InterstitialAdCard";
import ReorderAdModal from "./components/ReorderAdModal/ReorderAdModal";
import { formatLocalDateTime } from "utils/dateFormatter";
import AdDetailDialog from "../Analytics/components/AdDetailDialog/AdDetailDialog";

interface InterstitialAdListState {
    ads: any[];
    loading: boolean;
    searchTerm: string;
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    addModalOpen: boolean;
    reorderModalOpen: boolean;
    editingAd: any | null;
    isDeleteDialogOpen: boolean;
    deletingAd: any | null;
    deleteError: string | null;
    detailAdId: number | null;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_interstitial_ad_list_page_size";

class InterstitialAdListScreenClass extends Component<any, InterstitialAdListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 20;

        this.state = {
            ads: [],
            loading: false,
            searchTerm: "",
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            addModalOpen: false,
            reorderModalOpen: false,
            editingAd: null,
            isDeleteDialogOpen: false,
            deletingAd: null,
            deleteError: null,
            detailAdId: null,
        };
    }

    componentDidMount() {
        this.loadAds();
    }

    loadAds = async () => {
        try {
            this.setState({ loading: true });

            // 로그인 시 선택한 테넌트 가져오기
            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                Title: this.state.searchTerm || undefined,
                TenantSeqId: savedTenantId ? parseInt(savedTenantId, 10) : undefined,
            };

            const res = await request("InterstitialAd.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const ads = (pagedList.items || []).map((ad: any) => {
                    // Check if timezone exists (Z or +HH:MM or -HH:MM)
                    const hasTimezone = ad.crtDt && /(Z|[+-]\d{2}:?\d{2})$/.test(ad.crtDt);

                    return {
                        ...ad,
                        formattedStartDate: ad.startDate
                            ? new Date(ad.startDate).toLocaleDateString()
                            : "-",
                        formattedEndDate: ad.endDate
                            ? new Date(ad.endDate).toLocaleDateString()
                            : "-",
                        created: ad.crtDt ? formatLocalDateTime(ad.crtDt) : "-",
                        // If timezone exists, use as is. If not, append Z (assuming UTC from backend if no TZ).
                        // Note: dateFormatter defaults to KST (+09:00) if no TZ, but here we assume 'naked' dates might be UTC needing Z.
                        // Given the log showed explicit +09:00, this logic will preserve it correctly.
                        rawCrtDt: ad.crtDt ? (hasTimezone ? ad.crtDt : `${ad.crtDt}Z`) : null,
                    };
                });

                this.setState({
                    ads,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("전면광고 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = event.target.value;
        this.setState({ searchTerm, pageNumber: 1 }, () => {
            this.loadAds();
        });
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadAds();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadAds();
        });
    };

    handleAddModalOpen = () => {
        this.setState({ addModalOpen: true });
    };

    handleAddModalClose = () => {
        this.setState({ addModalOpen: false, editingAd: null });
    };

    handleAddSuccess = () => {
        this.setState({ pageNumber: 1, searchTerm: "" }, () => {
            this.loadAds();
        });
    };

    handleEdit = (ad: any) => {
        this.setState({
            editingAd: ad,
            addModalOpen: true,
        });
    };

    handleOpenDeleteDialog = (ad: any) => {
        this.setState({ deletingAd: ad, isDeleteDialogOpen: true });
    };

    handleCloseDeleteDialog = () => {
        this.setState({ isDeleteDialogOpen: false, deletingAd: null, deleteError: null });
    };

    handleConfirmDelete = async () => {
        if (!this.state.deletingAd) return;

        try {
            this.setState({ loading: true, deleteError: null });
            await request('InterstitialAd.delete', {
                paramz: {},
                pathParams: { id: this.state.deletingAd.seqId.toString() }
            });
            this.setState({ loading: false, isDeleteDialogOpen: false, deletingAd: null });
            this.loadAds();
        } catch (error: any) {
            console.error('광고 삭제 실패:', error);
            this.setState({ loading: false, deleteError: error?.response?.data?.message || '광고 삭제 중 오류가 발생했습니다.' });
        }
    };

    // Assuming handleAdAdded is intended to be the same as handleAddSuccess for now
    handleAdAdded = () => {
        this.handleAddSuccess();
    };

    handleOpenDetail = (seqId: number) => {
        this.setState({ detailAdId: seqId });
    };

    handleCloseDetail = () => {
        this.setState({ detailAdId: null });
    };

    render() {
        const { loading, searchTerm, ads } = this.state;
        const { darkMode } = this.props; // Destructure darkMode from props

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>
                    {/* 검색 영역 */}
                    <Card>
                        <MDBox p={3}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Icon fontSize="medium">fullscreen</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        전면광고 목록 ( INTERSTITIAL AD LIST )
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {/* 광고가 2개 이상일 때만 순서 변경 버튼 표시 */}
                                    {this.state.totalCount >= 2 && (
                                        <MDButton
                                            variant="outlined"
                                            color="info"
                                            onClick={() => this.setState({ reorderModalOpen: true })}
                                        >
                                            <Icon fontSize="small" sx={{ mr: 0.5 }}>
                                                swap_vert
                                            </Icon>
                                            광고 순서 변경
                                        </MDButton>
                                    )}
                                    <MDButton
                                        variant="gradient"
                                        color="info"
                                        onClick={this.handleAddModalOpen}
                                    >
                                        <Icon fontSize="small" sx={{ mr: 0.5 }}>
                                            add
                                        </Icon>
                                        전면광고 추가
                                    </MDButton>
                                </MDBox>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Autocomplete
                                        disableClearable
                                        value={this.state.pageSize.toString()}
                                        options={["5", "10", "15", "20", "25", "30", "40", "50"]}
                                        renderInput={(params) => (
                                            <MDInput {...params} />
                                        )}
                                        size="small"
                                        sx={{ width: "5rem" }}
                                        onChange={(_, newValue) => {
                                            if (newValue) {
                                                this.handlePageSizeChange(Number(newValue));
                                            }
                                        }}
                                    />
                                    <MDTypography variant="caption" color="secondary">
                                        &nbsp;&nbsp;entries per page
                                    </MDTypography>
                                </MDBox>

                                <MDBox width="12rem" ml="auto">
                                    <MDInput
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={this.handleSearch}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Icon fontSize="small" sx={{ color: '#9e9e9e' }}>
                                                        search
                                                    </Icon>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </MDBox>
                            </MDBox>
                        </MDBox>
                    </Card>

                    {/* 리스트 영역 */}
                    <MDBox mt={3}>
                        {loading ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        ) : ads.length === 0 ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDTypography variant="h6" color="text">
                                    {searchTerm ? "검색 결과가 없습니다." : "등록된 전면광고가 없습니다."}
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <>
                                <Grid container spacing={3}>
                                    {ads.map((ad: any) => {
                                        const gridProps = this.props.miniSidenav
                                            ? { xs: 12, sm: 6, md: 4, lg: 4, xl: 2.4 }
                                            : { xs: 12, sm: 6, md: 4, lg: 4, xl: 4 };

                                        const sxProps = this.props.miniSidenav
                                            ? {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '16.666667%',
                                                    maxWidth: '16.666667%'
                                                }
                                            }
                                            : {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '20%',
                                                    maxWidth: '20%'
                                                }
                                            };

                                        return (
                                            <Grid item {...gridProps} sx={sxProps} key={ad.seqId}>
                                                <InterstitialAdCard
                                                    ad={ad}
                                                    onClick={(ad) => this.handleOpenDetail(ad.seqId)}
                                                    onEdit={this.handleEdit}
                                                    onDelete={() => this.handleOpenDeleteDialog(ad)}
                                                    darkMode={this.props.darkMode}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {/* 페이지네이션 */}
                                {this.state.totalCount > 0 && (
                                    <MDBox mt={4} display="flex" justifyContent="center">
                                        <Pagination
                                            count={Math.ceil(this.state.totalCount / this.state.pageSize)}
                                            page={this.state.pageNumber}
                                            onChange={(_, page) => this.handlePageChange(page - 1)}
                                            color="primary"
                                            size="large"
                                            showFirstButton
                                            showLastButton
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                                },
                                                '& .Mui-selected': {
                                                    backgroundColor: '#49a3f1 !important',
                                                    color: '#ffffff !important',
                                                },
                                            }}
                                        />
                                    </MDBox>
                                )}
                            </>
                        )}
                    </MDBox>
                </MDBox>
                <Footer />
                <AddInterstitialAdModal
                    open={this.state.addModalOpen}
                    editingAd={this.state.editingAd}
                    onClose={this.handleAddModalClose}
                    onSuccess={this.handleAdAdded}
                    darkMode={this.props.darkMode}
                />

                {/* 광고 순서 변경 모달 */}
                <ReorderAdModal
                    open={this.state.reorderModalOpen}
                    onClose={() => this.setState({ reorderModalOpen: false })}
                    onSuccess={this.loadAds}
                    ads={this.state.ads.map(ad => ({
                        seqId: ad.seqId,
                        title: ad.title,
                        displayOrder: this.state.ads.findIndex(a => a.seqId === ad.seqId) + 1,
                    }))}
                    darkMode={darkMode}
                />

                {/* 삭제 확인 다이얼로그 */}
                <Dialog
                    open={this.state.isDeleteDialogOpen}
                    onClose={this.handleCloseDeleteDialog}
                    PaperProps={{
                        sx: {
                            backgroundColor: this.props.darkMode ? '#1a2035' : '#fff',
                            backgroundImage: 'none',
                        }
                    }}
                >
                    <DialogTitle sx={{ color: this.props.darkMode ? '#fff' : '#000' }}>
                        전면광고 삭제 확인
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                            "{this.state.deletingAd?.title}" 광고를 삭제하시겠습니까?<br />
                            이 작업은 취소할 수 없습니다.
                        </DialogContentText>
                        {this.state.deleteError && (
                            <MDBox mt={2}>
                                <p style={{ color: '#f44336', fontSize: '0.875rem', margin: 0 }}>
                                    {this.state.deleteError}
                                </p>
                            </MDBox>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={this.handleCloseDeleteDialog} color="secondary">
                            취소
                        </MDButton>
                        <MDButton
                            onClick={this.handleConfirmDelete}
                            variant="gradient"
                            color="error"
                            disabled={loading}
                        >
                            {loading ? '삭제 중...' : '삭제'}
                        </MDButton>
                    </DialogActions>
                </Dialog>

                {/* 광고 상세 통계 팝업 */}
                <AdDetailDialog
                    open={!!this.state.detailAdId}
                    onClose={this.handleCloseDetail}
                    seqId={this.state.detailAdId || 0}
                // 리스트 화면에서는 기간 필터가 없으므로 전체 기간(기본값) 조회
                />
            </DashboardLayout>
        );
    }
}

function InterstitialAdListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode, miniSidenav } = controller;

    return <InterstitialAdListScreenClass {...props} darkMode={darkMode} miniSidenav={miniSidenav} />;
}

export default InterstitialAdListScreen;
